import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

type AxiosConfigWithMetadata = InternalAxiosRequestConfig & {
    metadata?: {
        axiosId: string;
    };
};

const getAuthToken = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');

const TOKEN_ENDPOINT_PATH = '/v1/auth/v1/token';
const TRACEPARENT_HEADER = 'traceparent';
const REPORT_ERROR_ENDPOINT_PATH = '/v1/timekeeper/v1/report_error';

const randomHex = (bytes: number): string => {
    const buffer = new Uint8Array(bytes);

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(buffer);
    } else {
        for (let index = 0; index < buffer.length; index += 1) {
            buffer[index] = Math.floor(Math.random() * 256);
        }
    }

    return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const generateTraceparent = (): string => {
    let traceId = randomHex(16);
    while (/^0+$/.test(traceId)) {
        traceId = randomHex(16);
    }

    let parentId = randomHex(8);
    while (/^0+$/.test(parentId)) {
        parentId = randomHex(8);
    }

    return `00-${traceId}-${parentId}-01`;
};

const readHeaderValue = (headers: any, headerName: string): string | null => {
    if (!headers) return null;

    const normalizedHeaderName = headerName.toLowerCase();

    if (typeof headers.get === 'function') {
        const value = headers.get(headerName) ?? headers.get(normalizedHeaderName);
        return typeof value === 'string' && value.trim() !== '' ? value : null;
    }

    const value = headers[headerName] ?? headers[normalizedHeaderName];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
};

const extractRequestTraceparent = (error: any): string | null => {
    return readHeaderValue(error?.config?.headers, TRACEPARENT_HEADER);
};

const extractTraceIdFromTraceparent = (traceparent: string): string | null => {
    const traceparentParts = traceparent.split('-');
    if (traceparentParts.length !== 4) {
        return null;
    }

    const traceId = traceparentParts[1];
    if (!/^[a-f0-9]{32}$/i.test(traceId)) {
        return null;
    }

    return traceId;
};

const buildReportErrorEndpoint = (): string | null => {
    const gatewayBaseUrl = `${import.meta.env.VITE_TK_GW_DOMAIN || ''}`.replace(/\/+$/, '');
    if (!gatewayBaseUrl) {
        return null;
    }

    return `${gatewayBaseUrl}${REPORT_ERROR_ENDPOINT_PATH}`;
};

const parseErrorData = (data: any): any => {
    if (typeof data !== 'string') {
        return data;
    }

    try {
        return JSON.parse(data);
    } catch {
        return data;
    }
};

const normalizeHeaders = (headers: any): Record<string, any> => {
    if (!headers) return {};
    if (typeof headers.toJSON === 'function') {
        return headers.toJSON();
    }

    return { ...headers };
};

const buildFullRequestUrl = (error: any): string => {
    const baseURL = error?.config?.baseURL || '';
    const url = error?.config?.url || '';

    if (!baseURL) return url;
    if (/^https?:\/\//i.test(url)) return url;

    const normalizedBase = String(baseURL).replace(/\/+$/, '');
    const normalizedPath = String(url).replace(/^\/+/, '');
    return `${normalizedBase}/${normalizedPath}`;
};

const buildBrowserData = () => {
    if (typeof window === 'undefined') {
        return {};
    }

    const userAgentData = (navigator as any).userAgentData;
    const currentLocation = window.location;
    const queryParams = Object.fromEntries(new URLSearchParams(currentLocation.search).entries());

    return {
        user_agent: navigator.userAgent,
        browser_name: userAgentData?.brands?.[0]?.brand ?? null,
        browser_version: userAgentData?.brands?.[0]?.version ?? null,
        language: navigator.language,
        languages: navigator.languages,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        platform: navigator.platform,
        os: navigator.platform,
        device_memory_gb: (navigator as any).deviceMemory ?? null,
        cpu_cores: navigator.hardwareConcurrency ?? null,
        cookies_enabled: navigator.cookieEnabled,
        origin: currentLocation.origin,
        current_url: currentLocation.href,
        pathname: currentLocation.pathname,
        search: currentLocation.search,
        query_params: queryParams,
        hash: currentLocation.hash,
        route: `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`,
        screen: {
            width: window.screen?.width,
            height: window.screen?.height,
            avail_width: window.screen?.availWidth,
            avail_height: window.screen?.availHeight,
            pixel_ratio: window.devicePixelRatio,
        },
        viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
        },
    };
};

const buildUserData = (token: string | null) => {
    if (!token) {
        return {
            username: localStorage.getItem('username'),
            user_id: localStorage.getItem('user_id'),
        };
    }

    const payloadPart = token.split('.')[1];
    if (!payloadPart) {
        return {
            username: localStorage.getItem('username'),
            user_id: localStorage.getItem('user_id'),
        };
    }

    try {
        const decodedPayload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
        return {
            username: decodedPayload?.username ?? null,
            user_id: decodedPayload?.user_id ?? null,
            profile_id: decodedPayload?.profile_id ?? null,
            client: decodedPayload?.client ?? null,
        };
    } catch {
        return {
            username: localStorage.getItem('username'),
            user_id: localStorage.getItem('user_id'),
        };
    }
};

const captureScreenshot = async (): Promise<string | null> => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return null;
    }

    try {
        const html2canvasModule = await import('html2canvas');
        const html2canvas = ((html2canvasModule as any).default ?? html2canvasModule) as any;
        const canvas = await html2canvas(document.body, {
            backgroundColor: null,
            logging: false,
            useCORS: true,
            scale: 1,
        });

        return canvas.toDataURL('image/png');
    } catch (captureError) {
        console.error('[Axios][report-error] screenshot_failed', captureError);
        return null;
    }
};

const reportAxiosError = async (error: any, traceId: string, traceparent: string): Promise<void> => {
    const endpoint = buildReportErrorEndpoint();
    if (!endpoint) {
        console.warn('[Axios][report-error] missing VITE_TK_GW_DOMAIN');
        return;
    }

    const token = getAuthToken();
    const authorization = token ? `Bearer ${token}` : null;
    const screenshotBase64 = await captureScreenshot();
    const payload = {
        trace_id: traceId,
        traceparent,
        timestamp: new Date().toISOString(),
        authorization,
        failed_request: {
            method: error?.config?.method,
            url: error?.config?.url,
            base_url: error?.config?.baseURL,
            full_url: buildFullRequestUrl(error),
        },
        request_data: parseErrorData(error?.config?.data),
        request_headers: normalizeHeaders(error?.config?.headers),
        response_received: {
            status: error?.response?.status,
            status_text: error?.response?.statusText,
            data: error?.response?.data,
            headers: normalizeHeaders(error?.response?.headers),
        },
        user: buildUserData(token),
        browser: buildBrowserData(),
        screenshot_base64: screenshotBase64,
    };

    try {
        await axios.post(endpoint, payload, {
            headers: {
                'Content-Type': 'application/json',
                ...(authorization ? { Authorization: authorization } : {}),
            },
            timeout: 1000 * 30,
        });
    } catch (reportError) {
        console.error('[Axios][report-error] request_failed', reportError);
    }
};

const reportHttpAxiosError = (error: any): void => {
    const statusCode = Number(error?.response?.status);
    if (!Number.isFinite(statusCode) || statusCode < 400) {
        return;
    }

    const traceparent = extractRequestTraceparent(error);
    if (!traceparent) {
        return;
    }

    const traceId = extractTraceIdFromTraceparent(traceparent) ?? traceparent;
    void reportAxiosError(error, traceId, traceparent);
};

const clearSessionStorage = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refresh_expires_in');
    localStorage.removeItem('permissions');
    localStorage.removeItem('authenticated');
    sessionStorage.removeItem('authenticated');
};

const redirectToSignin = () => {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/signin')) {
        window.location.assign('/signin');
    }
};

let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
    pendingQueue.push(cb);
};

const onRefreshed = (token: string | null) => {
    pendingQueue.forEach((cb) => cb(token));
    pendingQueue = [];
};

const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        return null;
    }

    const clientId = `${import.meta.env.VITE_CLIENT_ID || ''}`.replace(/"/g, '').trim();
    if (!clientId) {
        return null;
    }

    const endpoint = `${import.meta.env.VITE_TK_GW_DOMAIN}${TOKEN_ENDPOINT_PATH}`;
    const body = new URLSearchParams();
    body.append('grant_type', 'refresh_token');
    body.append('client_id', clientId);
    body.append('refresh_token', refreshToken);

    try {
        const response = await axios.post(endpoint, body, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            timeout: 1000 * 30,
            // @ts-expect-error: marca interna para evitar loops del interceptor
            _skipAuthRefresh: true,
        });
        const data = response.data;
        if (!data?.access_token) {
            return null;
        }
        localStorage.setItem('access_token', data.access_token);
        window.dispatchEvent(new Event('authorization:token-updated'));
        if (data.token_type) localStorage.setItem('token_type', data.token_type);
        if (data.expires_in) localStorage.setItem('expires_in', String(data.expires_in));
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        if (data.refresh_expires_in) {
            localStorage.setItem('refresh_expires_in', String(data.refresh_expires_in));
        }
        return data.access_token as string;
    } catch {
        return null;
    }
};

// Evita refrescos concurrentes: la rotación del refresh_token revoca la familia ante reuso.
let refreshInFlight: Promise<string | null> | null = null;

const refreshSession = (): Promise<string | null> => {
    if (!refreshInFlight) {
        refreshInFlight = refreshAccessToken().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

const interceptRequest = (config: AxiosConfigWithMetadata) => {
    // Actualizar el token en cada petición
    const token = getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers.traceparent = generateTraceparent();

    if (typeof window !== 'undefined') {
        config.headers['X-Client-Route'] = getCurrentRoute();
        config.headers['X-Client-Url'] = getCurrentUrl();
    }

    return config;
};

const interceptSuccessResponse = (response: AxiosResponse) => {
    return response;
};

const interceptorErrorResponse = async (error: any) => {
    const originalRequest = error?.config as (AxiosConfigWithMetadata & { _retry?: boolean; _skipAuthRefresh?: boolean }) | undefined;
    const status = error?.response?.status;
    const hasResponse = !!error?.response;

    if (!originalRequest || originalRequest._skipAuthRefresh) {
        reportHttpAxiosError(error);
        return Promise.reject(error);
    }

    const url = `${originalRequest.url || ''}`;
    const isTokenEndpoint = url.includes(TOKEN_ENDPOINT_PATH);

    if (isTokenEndpoint) {
        reportHttpAxiosError(error);
        clearSessionStorage();
        redirectToSignin();
        return Promise.reject(error);
    }

    if (status === 403) {
        window.dispatchEvent(new CustomEvent('authorization:forbidden'));
        return Promise.reject(error);
    }

    const shouldTryRefresh = status === 401 || (!hasResponse && !!getRefreshToken());
    if (!shouldTryRefresh) {
        reportHttpAxiosError(error);
        return Promise.reject(error);
    }

    if (originalRequest._retry) {
        reportHttpAxiosError(error);
        clearSessionStorage();
        redirectToSignin();
        return Promise.reject(error);
    }
    originalRequest._retry = true;

    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            subscribeTokenRefresh((newToken) => {
                if (!newToken) {
                    reject(error);
                    return;
                }
                originalRequest.headers = originalRequest.headers || ({} as any);
                (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
                resolve(axios(originalRequest));
            });
        });
    }

    isRefreshing = true;
    const newToken = await refreshSession();
    isRefreshing = false;
    onRefreshed(newToken);

    if (!newToken) {
        reportHttpAxiosError(error);
        clearSessionStorage();
        redirectToSignin();
        return Promise.reject(error);
    }

    originalRequest.headers = originalRequest.headers || ({} as any);
    (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
    return axios(originalRequest);
};

// Instancia base de axios
const instance = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}`,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
    },
    timeout: 2 * 60 * 1000,
});

// Instancia para timekeeper
const instanceTimekeeper = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/timekeeper`,
    timeout: 1000 * 60,
});

// Instancia para empleados
const instanceEmployee = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/employee`,
    timeout: 1000 * 60,
});

const instanceSchedule = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/schedule`,
    timeout: 1000 * 60,
});

// Instancia para auth
const instanceAuth = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/auth`,
    timeout: 1000 * 60,
});

// Instancia para Reports
const instanceReports = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/custom_reports`,
    timeout: 1000 * 60,
});

// Instancia para Virtualization
const instanceVirtualization = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/virtual_interpretation`,
    timeout: 1000 * 60,
});

// Instancia para MDM PSC
const instanceMdmPsc = axios.create({
    baseURL: `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/mdm_psc`,
    timeout: 1000 * 60,
});

const getCurrentUrl = (): string => {
    return window.location.href;
};

const getCurrentRoute = (): string => {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

// Aplicar interceptores a las instancias
instance.interceptors.request.use(interceptRequest);
instance.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceTimekeeper.interceptors.request.use(interceptRequest);
instanceTimekeeper.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceEmployee.interceptors.request.use(interceptRequest);
instanceEmployee.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceSchedule.interceptors.request.use(interceptRequest);
instanceSchedule.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceAuth.interceptors.request.use(interceptRequest);
instanceAuth.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceReports.interceptors.request.use(interceptRequest);
instanceReports.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceVirtualization.interceptors.request.use(interceptRequest);
instanceVirtualization.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

instanceMdmPsc.interceptors.request.use(interceptRequest);
instanceMdmPsc.interceptors.response.use(interceptSuccessResponse, interceptorErrorResponse);

export { instance, instanceTimekeeper, instanceEmployee, instanceSchedule, instanceAuth, instanceReports, instanceVirtualization, instanceMdmPsc, refreshSession };
